import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import VideoLinkPreview from '../../components/VideoLinkPreview';
import ImageUrlComponent from '../../components/ImageUrlComponent';
import { Course, Module } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Training/Courses/PathItems';
import { Lesson } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Training/Courses/Lessons/PathItems';
import { useCourses } from '../../providers/CourseProvider';
import { OperationRequest } from '@peercolab/engine';
import { CourseManagment } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';

interface EditLessonDetailsProps {
  course: Course
  module: Module
  lesson: Lesson;
  patchField: <R, V>(operation: OperationRequest<unknown, R>, newValue: V, currentValue: V, revertHandler: (value: V) => void) => Promise<void>;
}

const EditLessonDetails: React.FC<EditLessonDetailsProps> = ({ lesson, patchField }) => {
  const { courses } = useCourses();
  // Internal state for all fields
  const [name, setName] = useState(lesson.name);
  const [description, setDescription] = useState(lesson.details.description);
  const [longDescription, setLongDescription] = useState(lesson.details.longDescription);
  const [durationMinutes, setDurationMinutes] = useState(lesson.details.duration?.toString() || '');
  const [image, setImage] = useState(lesson.media.thumbnail.url);
  const [videoLink, setVideoLink] = useState(lesson.media.video.url);

  useEffect(() => {
    setName(lesson.name);
    setDescription(lesson.details.description);
    setLongDescription(lesson.details.longDescription);
    setDurationMinutes(lesson.details.duration?.toString() || '');
    setImage(lesson.media.thumbnail.url);
    setVideoLink(lesson.media.video.url);
  }, [courses, lesson])

  return (
    <div style={{ display: 'flex', gap: 32 }}>
      {/* Left column */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Trainer moved to right column */}
        <TextField
          label="Lesson Name"
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={e => patchField(
            CourseManagment.updateLessonName({ name }),
            name,
            lesson.name,
            setName
          )}
          sx={{ minWidth: 250 }}
        />
        <TextField
          label="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          onBlur={e => patchField(
            CourseManagment.updateLessonDescription({ description }),
            description,
            lesson.details.description,
            setDescription
          )}
          multiline
          minRows={2}
        />
        <TextField
          label="Long Description"
          value={longDescription}
          onChange={e => setLongDescription(e.target.value)}
          onBlur={e => patchField(
            CourseManagment.updateLessonLongDescription({ longDescription }),
            longDescription,
            lesson.details.longDescription,
            setLongDescription
          )}
          multiline
          minRows={20}
        />
      </div>
      {/* Right column */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <TextField
          label="Duration (minutes)"
          type="number"
          value={durationMinutes}
          onChange={e => setDurationMinutes(e.target.value)}
          onBlur={e => {
            const duration = parseInt(durationMinutes || '0');
            patchField(
              CourseManagment.updateLessonDuration({ duration }),
              duration,
              lesson.details.duration,
              val => setDurationMinutes(val?.toString() || '')
            );
          }}
          sx={{ width: 180 }}
          inputProps={{ min: 0 }}
        />
        <ImageUrlComponent value={image} onChange={(url) => {
          patchField(
            CourseManagment.updateLessonThumbnailUrl({ url: url ?? '' }),
            url ?? '',
            lesson.media.thumbnail.url,
            setImage
          );
          setImage(url ?? '');
        }} />
        <VideoLinkPreview value={videoLink} onChange={link => {
          patchField(
            CourseManagment.updateLessonVideoUrl({ url: link }),
            link,
            lesson.media.video.url,
            setVideoLink
          );
          setVideoLink(link);
        }} />
      </div>
    </div>
  );
};

export default EditLessonDetails;
